// The demo dataset: the three projects every visitor starts with and the
// five templates a request is seeded from while the pipeline runs. Copied
// from the iOS DemoData.json and owned here; checks/demo.test.ts fails on
// any drift from that file. The ids are fixed on purpose: two devices' copies
// of a demo project merge by editedAt at sign-in instead of doubling.
import { titleTrimmed, type Millis, type Project, type Step } from './plan';

/** A canned plan and the request words that pick it. The one with no keywords is the fallback. */
export interface Template { keywords: string[]; project: Project }

export const DEMO_PROJECTS: readonly Project[] = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    title: 'Cedar Birdhouse',
    summary: 'A cozy A-frame home for backyard songbirds.',
    artwork: 'blueprint-birdhouse',
    steps: [
      { id: '00000000-0000-0000-0000-000000000101', title: 'Cut cedar boards to size', done: true, bundle: 'Cut the pieces' },
      { id: '00000000-0000-0000-0000-000000000102', title: 'Drill the 1¼ inch entry hole', done: true, bundle: 'Cut the pieces' },
      { id: '00000000-0000-0000-0000-000000000103', title: 'Assemble walls and floor', done: false, bundle: 'Assemble the house' },
      { id: '00000000-0000-0000-0000-000000000104', title: 'Attach the roof panels', done: false, bundle: 'Assemble the house' },
      { id: '00000000-0000-0000-0000-000000000105', title: 'Sand all edges smooth', done: false, bundle: 'Finish and install' },
      { id: '00000000-0000-0000-0000-000000000106', title: 'Mount on a pole or fence post', done: false, bundle: 'Finish and install' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000000111', title: 'Cedar fence picket', done: true, bundle: 'Cut and shape', reason: 'Weather-resistant stock that won\'t rot outdoors.' },
      { id: '00000000-0000-0000-0000-000000000114', title: '1¼ inch hole saw bit', done: false, bundle: 'Cut and shape', reason: 'Cuts a clean entry hole sized for songbirds.' },
      { id: '00000000-0000-0000-0000-000000000115', title: 'Sandpaper, 120 grit', done: false, bundle: 'Cut and shape', reason: 'Smooths every edge so fledglings can\'t catch a splinter.' },
      { id: '00000000-0000-0000-0000-000000000112', title: 'Wood glue', done: true, bundle: 'Join and mount', reason: 'Bonds the joints before the nails go in.' },
      { id: '00000000-0000-0000-0000-000000000113', title: 'Galvanized nails', done: false, bundle: 'Join and mount', reason: 'Rust-proof fasteners that hold the walls tight.' },
    ],
    videos: [],
    tips: [
      'Never paint the inside — birds prefer bare wood.',
      'Drill a few small holes in the floor for drainage.',
      'Face the entry hole away from prevailing wind.',
    ],
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    title: 'Long-press to delete',
    summary: 'A quick lesson: hold a card to tidy it off your list.',
    artwork: 'blueprint-generic',
    steps: [
      { id: '00000000-0000-0000-0000-000000000201', title: 'Find this card on the home screen', done: true, bundle: 'Try the gesture' },
      { id: '00000000-0000-0000-0000-000000000202', title: 'Press and hold it until a menu pops up', done: true, bundle: 'Try the gesture' },
      { id: '00000000-0000-0000-0000-000000000203', title: 'Tap “Delete project” in that menu', done: true, bundle: 'Clear it away' },
      { id: '00000000-0000-0000-0000-000000000204', title: 'Watch it vanish — your list just got tidier', done: true, bundle: 'Clear it away' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000000211', title: 'One steady thumb', done: true, bundle: 'What you\'ll need', reason: 'The whole trick is a press that lasts a beat longer than a tap.' },
      { id: '00000000-0000-0000-0000-000000000212', title: 'Ten spare seconds', done: true, bundle: 'What you\'ll need', reason: 'Long-press, tap, done — that\'s the entire lesson.' },
    ],
    videos: [],
    tips: [
      'The long-press menu works on every card, not just this one.',
      'Deleting is final — there\'s no undo — so hold only what you mean to remove.',
      'Want to rename your list or hide the cards up top? That lives in Settings, under “Home screen”.',
    ],
  },
  {
    id: '00000000-0000-0000-0000-000000000003',
    title: 'Get the Wi-Fi working again',
    summary: 'Rogers internet dropped? Check whether it\'s really down, then bring it back.',
    artwork: 'blueprint-generic',
    request: 'How do I get my Wi-Fi/internet working again? With Rogers. Did it go down again? How do I check?',
    photoHash: 'd6de5499d745866c1284fa5e8ac0cc78',
    steps: [
      { id: '00000000-0000-0000-0000-000000000301', title: 'Check the router\'s lights — steady means online, blinking means it\'s still trying', done: true, bundle: 'Is it really down?' },
      { id: '00000000-0000-0000-0000-000000000302', title: 'Open the MyRogers app or the Rogers outage map for your area', done: true, bundle: 'Is it really down?' },
      { id: '00000000-0000-0000-0000-000000000303', title: 'Load a site on cellular data to rule out your own device', done: false, bundle: 'Is it really down?' },
      { id: '00000000-0000-0000-0000-000000000304', title: 'Unplug the Rogers modem for 30 seconds, then plug it back in', done: false, bundle: 'Reset the connection' },
      { id: '00000000-0000-0000-0000-000000000305', title: 'Wait 2–3 minutes for the online light to go solid', done: false, bundle: 'Reset the connection' },
      { id: '00000000-0000-0000-0000-000000000306', title: 'Reconnect your device and test the Wi-Fi', done: false, bundle: 'Reset the connection' },
      { id: '00000000-0000-0000-0000-000000000307', title: 'Still dark? Reboot from the MyRogers app or call Rogers support', done: false, bundle: 'If it\'s still out' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000000311', title: 'The MyRogers app', done: true, bundle: 'Check', reason: 'Shows outages in your area and can reboot the modem remotely.' },
      { id: '00000000-0000-0000-0000-000000000312', title: 'A phone on cellular data', done: false, bundle: 'Check', reason: 'Tells you whether the problem is the Rogers line or just your Wi-Fi.' },
      { id: '00000000-0000-0000-0000-000000000313', title: 'A paperclip', done: false, bundle: 'Reset', reason: 'Presses the recessed reset button when a plain power-cycle isn\'t enough.' },
    ],
    videos: [],
    tips: [
      'A blinking white or green light usually means the modem is still syncing — give it a few minutes before assuming the worst.',
      'The MyRogers app can reboot your modem without you crawling behind the TV to unplug it.',
      'If the neighbours are down too, it\'s an outage on Rogers\' end — no amount of resetting will fix it.',
    ],
  },
];

export const TEMPLATES: readonly Template[] = [
  { keywords: ['bird','birdhouse','nest'], project: {
    id: '00000000-0000-0000-0000-000000001001',
    title: 'Cedar Birdhouse',
    summary: 'A cozy A-frame home for backyard songbirds.',
    artwork: 'blueprint-birdhouse',
    steps: [
      { id: '00000000-0000-0000-0000-000000001011', title: 'Cut cedar boards to size', done: false, bundle: 'Cut the pieces' },
      { id: '00000000-0000-0000-0000-000000001012', title: 'Drill the 1¼ inch entry hole', done: false, bundle: 'Cut the pieces' },
      { id: '00000000-0000-0000-0000-000000001013', title: 'Assemble walls and floor', done: false, bundle: 'Assemble the house' },
      { id: '00000000-0000-0000-0000-000000001014', title: 'Attach the roof panels', done: false, bundle: 'Assemble the house' },
      { id: '00000000-0000-0000-0000-000000001015', title: 'Sand all edges smooth', done: false, bundle: 'Finish and install' },
      { id: '00000000-0000-0000-0000-000000001016', title: 'Mount on a pole or fence post', done: false, bundle: 'Finish and install' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000001021', title: 'Cedar fence picket', done: false, bundle: 'Cut and shape', reason: 'Weather-resistant stock that won\'t rot outdoors.' },
      { id: '00000000-0000-0000-0000-000000001024', title: '1¼ inch hole saw bit', done: false, bundle: 'Cut and shape', reason: 'Cuts a clean entry hole sized for songbirds.' },
      { id: '00000000-0000-0000-0000-000000001025', title: 'Sandpaper, 120 grit', done: false, bundle: 'Cut and shape', reason: 'Smooths every edge so fledglings can\'t catch a splinter.' },
      { id: '00000000-0000-0000-0000-000000001022', title: 'Wood glue', done: false, bundle: 'Join and mount', reason: 'Bonds the joints before the nails go in.' },
      { id: '00000000-0000-0000-0000-000000001023', title: 'Galvanized nails', done: false, bundle: 'Join and mount', reason: 'Rust-proof fasteners that hold the walls tight.' },
    ],
    videos: [],
    tips: [
      'Never paint the inside — birds prefer bare wood.',
      'Drill a few small holes in the floor for drainage.',
      'Face the entry hole away from prevailing wind.',
    ],
  } },
  { keywords: ['faucet','leak','drip','sink','plumb'], project: {
    id: '00000000-0000-0000-0000-000000002001',
    title: 'Fix the Leaky Faucet',
    summary: 'Silence the drip and save a few gallons a day.',
    artwork: 'blueprint-faucet',
    steps: [
      { id: '00000000-0000-0000-0000-000000002011', title: 'Shut off the water under the sink', done: false, bundle: 'Shut down and open up' },
      { id: '00000000-0000-0000-0000-000000002012', title: 'Plug the drain so parts can\'t escape', done: false, bundle: 'Shut down and open up' },
      { id: '00000000-0000-0000-0000-000000002013', title: 'Remove the faucet handle', done: false, bundle: 'Shut down and open up' },
      { id: '00000000-0000-0000-0000-000000002014', title: 'Swap the worn O-ring and washer', done: false, bundle: 'Replace and reseal' },
      { id: '00000000-0000-0000-0000-000000002015', title: 'Reassemble and test for drips', done: false, bundle: 'Replace and reseal' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000002021', title: 'Adjustable wrench', done: false, bundle: 'Open the faucet', reason: 'Loosens the packing nut without rounding it off.' },
      { id: '00000000-0000-0000-0000-000000002024', title: 'Rag or towel', done: false, bundle: 'Open the faucet', reason: 'Catches drips and protects the sink\'s finish.' },
      { id: '00000000-0000-0000-0000-000000002022', title: 'Replacement O-ring kit', done: false, bundle: 'Reseal the valve', reason: 'The worn seal is what\'s letting the drip through.' },
      { id: '00000000-0000-0000-0000-000000002023', title: 'Plumber\'s grease', done: false, bundle: 'Reseal the valve', reason: 'Keeps the new O-ring supple and leak-free.' },
    ],
    videos: [],
    tips: [
      'Take the old O-ring to the store to match the size exactly.',
      'Wrap the wrench jaws in tape so they don\'t scratch the chrome.',
      'If the drip continues, the valve seat may need replacing.',
    ],
  } },
  { keywords: ['shelf','shelves','book','mount','hang'], project: {
    id: '00000000-0000-0000-0000-000000003001',
    title: 'Floating Shelves',
    summary: 'Invisible-bracket shelves, straight and sturdy.',
    artwork: 'blueprint-shelf',
    steps: [
      { id: '00000000-0000-0000-0000-000000003011', title: 'Find the wall studs', done: false, bundle: 'Mark the layout' },
      { id: '00000000-0000-0000-0000-000000003012', title: 'Mark a level line', done: false, bundle: 'Mark the layout' },
      { id: '00000000-0000-0000-0000-000000003013', title: 'Screw the hidden brackets into studs', done: false, bundle: 'Mount the shelves' },
      { id: '00000000-0000-0000-0000-000000003014', title: 'Slide the shelves on and load them up', done: false, bundle: 'Mount the shelves' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000003021', title: 'Stud finder', done: false, bundle: 'Find and mark', reason: 'Locates the studs strong enough to carry the load.' },
      { id: '00000000-0000-0000-0000-000000003022', title: 'Bubble level', done: false, bundle: 'Find and mark', reason: 'Keeps the shelf dead straight so books don\'t slide.' },
      { id: '00000000-0000-0000-0000-000000003023', title: 'Drill and bits', done: false, bundle: 'Fasten', reason: 'Drives the bracket screws deep into the studs.' },
      { id: '00000000-0000-0000-0000-000000003024', title: 'Floating shelf kit', done: false, bundle: 'Fasten', reason: 'The hidden brackets that make the shelf appear to float.' },
    ],
    videos: [],
    tips: [
      'Always hit at least one stud — drywall anchors alone will sag.',
      'Books are heavy: budget 20 lbs per bracket, minimum.',
    ],
  } },
  { keywords: ['plant','garden','herb','planter','flower'], project: {
    id: '00000000-0000-0000-0000-000000004001',
    title: 'Herb Garden Planter',
    summary: 'A window-box planter for basil, thyme, and friends.',
    artwork: 'blueprint-planter',
    steps: [
      { id: '00000000-0000-0000-0000-000000004011', title: 'Cut the box sides and bottom', done: false, bundle: 'Build the box' },
      { id: '00000000-0000-0000-0000-000000004012', title: 'Drill drainage holes in the base', done: false, bundle: 'Build the box' },
      { id: '00000000-0000-0000-0000-000000004013', title: 'Screw the box together', done: false, bundle: 'Build the box' },
      { id: '00000000-0000-0000-0000-000000004014', title: 'Line with landscape fabric', done: false, bundle: 'Plant it up' },
      { id: '00000000-0000-0000-0000-000000004015', title: 'Fill with soil and plant the herbs', done: false, bundle: 'Plant it up' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000004021', title: 'Cedar boards', done: false, bundle: 'Build the box', reason: 'Naturally rot-resistant wood safe for growing food.' },
      { id: '00000000-0000-0000-0000-000000004022', title: 'Exterior screws', done: false, bundle: 'Build the box', reason: 'Weatherproof fasteners that won\'t corrode outdoors.' },
      { id: '00000000-0000-0000-0000-000000004023', title: 'Potting soil', done: false, bundle: 'Plant it up', reason: 'Light, draining mix the herbs will root into.' },
      { id: '00000000-0000-0000-0000-000000004024', title: 'Herb starters', done: false, bundle: 'Plant it up', reason: 'Young plants for a harvest weeks sooner than seed.' },
    ],
    videos: [],
    tips: [
      'Skip pressure-treated wood for anything growing food.',
      'Herbs hate wet feet — more drainage holes than you think.',
      'Basil grows fast; give it its own corner.',
    ],
  } },
  { keywords: [], project: {
    id: '00000000-0000-0000-0000-000000005001',
    title: '',
    summary: 'A custom plan, sketched fresh from your idea.',
    artwork: 'blueprint-generic',
    steps: [
      { id: '00000000-0000-0000-0000-000000005011', title: 'Break the project into small steps', done: false, bundle: 'Plan and prep' },
      { id: '00000000-0000-0000-0000-000000005012', title: 'Gather tools and materials', done: false, bundle: 'Plan and prep' },
      { id: '00000000-0000-0000-0000-000000005013', title: 'Prep and protect the work area', done: false, bundle: 'Plan and prep' },
      { id: '00000000-0000-0000-0000-000000005014', title: 'Do the build, one step at a time', done: false, bundle: 'Build and finish' },
      { id: '00000000-0000-0000-0000-000000005015', title: 'Clean up and admire your handiwork', done: false, bundle: 'Build and finish' },
    ],
    equipment: [
      { id: '00000000-0000-0000-0000-000000005021', title: 'Tape measure', done: false, bundle: 'Measure and protect', reason: 'Gets every cut and placement right the first time.' },
      { id: '00000000-0000-0000-0000-000000005022', title: 'Safety glasses', done: false, bundle: 'Measure and protect', reason: 'Shields your eyes from dust and flying chips.' },
      { id: '00000000-0000-0000-0000-000000005023', title: 'Basic tool kit', done: false, bundle: 'Build', reason: 'The everyday drivers, pliers, and blades every build needs.' },
    ],
    videos: [],
    tips: [
      'Measure twice, cut once.',
      'Watch one video before buying anything.',
      'A small project finished beats a big project started.',
    ],
  } },
];

/** The template whose keywords the request mentions, else the fallback with no keywords. */
export function matchTemplate(request: string, templates: readonly Template[] = TEMPLATES): Template {
  const lowered = request.toLowerCase();
  const hit = templates.find((t) => t.keywords.some((k) => lowered.includes(k)));
  if (hit) return hit;
  const fallback = templates.find((t) => t.keywords.length === 0);
  if (!fallback) throw new Error('templates must include a fallback with no keywords');
  return fallback;
}

/**
 * A brand-new, unticked copy of a template with fresh identities. A template
 * with an empty title takes its title from the request, trimmed to what a
 * card can show. The request rides along as the regeneration input.
 */
export function instantiate(template: Template, request: string, now: Millis): Project {
  const { project } = template;
  const fresh = (item: Step): Step => {
    const copy: Step = { id: crypto.randomUUID(), title: item.title, done: false };
    if (item.bundle !== undefined) copy.bundle = item.bundle;
    if (item.reason !== undefined) copy.reason = item.reason;
    return copy;
  };
  return {
    id: crypto.randomUUID(),
    title: project.title === '' ? titleTrimmed(request) : project.title,
    summary: project.summary,
    artwork: project.artwork,
    steps: project.steps.map(fresh),
    equipment: project.equipment.map(fresh),
    videos: [...project.videos],
    tips: [...project.tips],
    request,
    createdAt: now,
  };
}

/**
 * How many boxes were ticked on each day, across steps and equipment, keyed
 * by the start of the day each one was last ticked. The caller supplies
 * startOfDay so the calendar stays theirs and this stays pure. An item ticked
 * before it had a clock does not plot, and a deleted project does not count.
 */
export function activityTally(projects: readonly Project[], startOfDay: (t: Millis) => Millis): Map<Millis, number> {
  const tally = new Map<Millis, number>();
  for (const p of projects) {
    if (p.deletedAt !== undefined) continue;
    for (const item of [...p.steps, ...p.equipment]) {
      if (!item.done || item.editedAt === undefined) continue;
      const day = startOfDay(item.editedAt);
      tally.set(day, (tally.get(day) ?? 0) + 1);
    }
  }
  return tally;
}
